import { PLAN_RANK } from './constants/complianceChecklists';
import type { PricingTierId } from './constants/pricingTiers';

export type RazzmatazzCapability = 'CompliCore' | 'CompliAdvantage' | 'CompliComplete';

export type RazzmatazzStep = {
  id: string;
  capability: RazzmatazzCapability;
  planMin: PricingTierId;
  title: [string, string];
  action: [string, string];
  dependsOn?: string;
};

/**
 * CompliCore is the studio you run by hand.
 * CompliAdvantage adds the watches.
 * CompliComplete adds the governed layer and the film desk.
 */
export const RAZZMATAZZ_WORKFLOW: RazzmatazzStep[] = [
  {
    id: 'brief',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['Write the brief', 'Escribe el brief'],
    action: ['Say who it is for, in one line. That line is the job for every page and draft in this project.', 'Di para quién es, en una línea. Esa línea es el trabajo de cada página y borrador de este proyecto.'],
  },
  {
    id: 'pages',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['Name the pages', 'Nombra las páginas'],
    action: ['Add the public paths this drop owns. One project does not have to be a new company.', 'Añade las rutas públicas de este lanzamiento. Un proyecto no tiene que ser una empresa nueva.'],
    dependsOn: 'brief',
  },
  {
    id: 'phrases',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['Pick the phrases', 'Elige las frases'],
    action: ['Write the phrases you want people to find. Volume and difficulty stay notes you enter.', 'Escribe las frases que quieres que la gente encuentre. El volumen y la dificultad son notas que tú escribes.'],
    dependsOn: 'brief',
  },
  {
    id: 'draft',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['Draft the piece', 'Redacta la pieza'],
    action: ['Start the page, post, or note. It stays a draft until someone reviews it.', 'Empieza la página, el post o la nota. Sigue en borrador hasta que alguien lo revise.'],
    dependsOn: 'pages',
  },
  {
    id: 'approval',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['Get the yes', 'Consigue el sí'],
    action: ['A person submits it and a person approves it. The studio does not approve itself.', 'Una persona lo envía y una persona lo aprueba. El estudio no se aprueba solo.'],
    dependsOn: 'draft',
  },
  {
    id: 'live',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['Mark it live', 'Márcalo en vivo'],
    action: [
      'Mark it live only after the yes. Page and email stay a record. Social drafts push through Socials when the adapter is on.',
      'Márcalo en vivo solo después del sí. Página y correo quedan como registro. Los borradores sociales se publican vía Socials si el adaptador está activo.',
    ],
    dependsOn: 'approval',
  },
  {
    id: 'recap',
    capability: 'CompliCore',
    planMin: 'basic',
    title: ['File the recap', 'Archiva el recap'],
    action: ['Write what shipped this week. The recap is a record, not an email send.', 'Escribe lo que salió esta semana. El recap es un registro, no un correo enviado.'],
    dependsOn: 'live',
  },
  {
    id: 'phrase-watch',
    capability: 'CompliAdvantage',
    planMin: 'advanced',
    title: ['Watch the phrases', 'Vigila las frases'],
    action: ['Note where each phrase sits. This watch is a CompliAdvantage capability. It does not buy a search index.', 'Anota dónde queda cada frase. Esta vigilancia es una capacidad de CompliAdvantage. No compra un índice de búsqueda.'],
    dependsOn: 'phrases',
  },
  {
    id: 'ai-mentions',
    capability: 'CompliAdvantage',
    planMin: 'advanced',
    title: ['Watch AI answers', 'Vigila las respuestas de IA'],
    action: ['Record whether an AI answer cites you. A person checks the answer. The studio does not crawl the model.', 'Registra si una respuesta de IA te cita. Una persona revisa la respuesta. El estudio no rastrea el modelo.'],
    dependsOn: 'phrases',
  },
  {
    id: 'prospects',
    capability: 'CompliAdvantage',
    planMin: 'advanced',
    title: ['Line up links', 'Alinea los enlaces'],
    action: ['List places that should point at this project. Prospects stay a list until a person marks one live.', 'Lista los sitios que deberían apuntar a este proyecto. Los prospectos siguen en lista hasta que una persona marca uno en vivo.'],
    dependsOn: 'pages',
  },
  {
    id: 'repeating-recap',
    capability: 'CompliAdvantage',
    planMin: 'advanced',
    title: ['Repeat the recap', 'Repite el recap'],
    action: ['Set the recap to weekly or monthly. CompliAdvantage keeps the cadence. It still does not send the mail.', 'Pon el recap en semanal o mensual. CompliAdvantage guarda la cadencia. Sigue sin enviar el correo.'],
    dependsOn: 'recap',
  },
  {
    id: 'consent',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Record consent', 'Registra el consentimiento'],
    action: ['Write who may be contacted, on which channel, and the date they agreed. A stop removes them from every list in this project. This record does not send the message.', 'Escribe a quién se puede contactar, por qué canal y la fecha en que aceptó. Un alto lo saca de todas las listas de este proyecto. Este registro no envía el mensaje.'],
    dependsOn: 'approval',
  },
  {
    id: 'suppression',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Name who to leave out', 'Nombra a quién dejar fuera'],
    action: ['List the people and companies this project must not contact, and why. The list is checked before anything is marked live.', 'Lista las personas y empresas que este proyecto no debe contactar, y por qué. La lista se revisa antes de marcar algo en vivo.'],
    dependsOn: 'consent',
  },
  {
    id: 'frequency',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Set the cap', 'Pon el límite'],
    action: ['Write how often one person may hear from this project. A later draft waits when the cap is already met.', 'Escribe cuántas veces una persona puede saber de este proyecto. Un borrador posterior espera si el límite ya se cumplió.'],
    dependsOn: 'consent',
  },
  {
    id: 'audience',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Name the audience', 'Nombra la audiencia'],
    action: ['Name the audience and the rule in one sentence. The rule is what a person can check, not a hidden score.', 'Nombra la audiencia y la regla en una frase. La regla es lo que una persona puede revisar, no un puntaje oculto.'],
    dependsOn: 'brief',
  },
  {
    id: 'journey',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Write the path', 'Escribe el recorrido'],
    action: ['Write what starts the path, what it waits for, and the branch. A person still marks each move. The path does not send mail, a text, or an ad.', 'Escribe qué inicia el recorrido, qué espera y la rama. Una persona sigue marcando cada paso. El recorrido no envía correo, texto ni anuncio.'],
    dependsOn: 'audience',
  },
  {
    id: 'score',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Score the account', 'Puntúa la cuenta'],
    action: ['Write the score and the reason in plain language, for the handoff. The studio does not invent the number.', 'Escribe el puntaje y la razón en lenguaje claro, para la entrega. El estudio no inventa el número.'],
    dependsOn: 'audience',
  },
  {
    id: 'handoff',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Hand it to sales', 'Entrégalo a ventas'],
    action: ['Name the person in Pursuit who owns the next step, and what they were given. Marketing keeps the record. Sales owns the conversation.', 'Nombra a la persona en Pursuit que tiene el siguiente paso, y qué se le entregó. Marketing guarda el registro. Ventas lleva la conversación.'],
    dependsOn: 'score',
  },
  {
    id: 'attribution',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Explain the result', 'Explica el resultado'],
    action: ['Pick first touch, last touch, or an even split, and name the deal it should explain. The number is a record a person enters. It is not a warehouse report.', 'Elige primer contacto, último contacto o un reparto parejo, y nombra el trato que debe explicar. El número es un registro que escribe una persona. No es un informe de almacén de datos.'],
    dependsOn: 'handoff',
  },
  {
    id: 'mail-auth',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Check the sending domain', 'Revisa el dominio de envío'],
    action: ['Record who may send for the domain, whether the signature matches, and what happens to mail that fails. A person checks the public records. Razzmatazz does not read them.', 'Registra quién puede enviar por el dominio, si la firma coincide y qué pasa con el correo que falla. Una persona revisa los registros públicos. Razzmatazz no los lee.'],
    dependsOn: 'approval',
  },
  {
    id: 'preference',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['List the topics', 'Lista los temas'],
    action: ['List the topics a person opted into, and the ones they refused. A draft stays off a topic they refused.', 'Lista los temas que una persona aceptó y los que rechazó. Un borrador no usa un tema que rechazó.'],
    dependsOn: 'consent',
  },
  {
    id: 'experiment',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Name the test', 'Nombra la prueba'],
    action: ['Name the holdout group and what would count as a win. Launching the test stays a person’s decision.', 'Nombra el grupo que queda fuera y qué contaría como victoria. Lanzar la prueba sigue siendo decisión de una persona.'],
    dependsOn: 'journey',
  },
  {
    id: 'hygiene',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Clean the names', 'Limpia los nombres'],
    action: ['Note the duplicate and the name you will keep. Two rows for one person do not both get a message.', 'Anota el duplicado y el nombre que vas a conservar. Dos filas de una persona no reciben las dos un mensaje.'],
    dependsOn: 'audience',
  },
  {
    id: 'film',
    capability: 'CompliComplete',
    planMin: 'enterprise',
    title: ['Lock the film', 'Cierra la pieza'],
    action: ['Review the treatment, the timed beats, the script, and the published picture. A person approves the pacing before production spend. The film desk does not publish the picture by itself.', 'Revisa el tratamiento, los beats con tiempo, el guion y la imagen publicada. Una persona aprueba el ritmo antes del gasto de producción. El escritorio de video no publica la imagen solo.'],
    dependsOn: 'approval',
  },
];

export const planFromTierName = (tierName: string): PricingTierId => {
  const name = tierName.trim().toLowerCase();
  if (name === 'complicomplete' || name === 'enterprise') {
    return 'enterprise';
  }
  if (name === 'compliadvantage' || name === 'advanced') {
    return 'advanced';
  }
  return 'basic';
};

export const stepIncludedInPlan = (step: RazzmatazzStep, plan: PricingTierId): boolean => (
  PLAN_RANK[step.planMin] <= PLAN_RANK[plan]
);

export const stepsForCapability = (capability: RazzmatazzCapability): RazzmatazzStep[] => (
  RAZZMATAZZ_WORKFLOW.filter((step) => step.capability === capability)
);
